import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PromptDialog,
  type PromptDraft,
  PromptList,
  useAddMyPrompt,
  useDeleteMyPrompt,
  useMyPrompts,
  useUpdateMyPrompt,
} from "@/features/prompts";
import { brandReviewOptions, type PromptPublic } from "@/lib/api";

/**
 * The client's prompt library.
 *
 * They read The Motherhood's prompts and cannot change them, which is the
 * ownership rule from the glossary. What they can do is add their own, and
 * start one of their own from one of The Motherhood's when they want it worded
 * differently: that opens the same dialog with the original filled in, and
 * nothing exists until they save it.
 */
export const Route = createFileRoute("/_authenticated/org/prompts")({
  component: PromptLibraryPage,
});

function PromptLibraryPage() {
  const { data: prompts, isPending, isError, refetch } = useMyPrompts();
  const review = useQuery({ ...brandReviewOptions(), meta: { silent: true } });
  const pillars = review.data?.config?.contentPillars ?? [];
  // An org whose setup The Motherhood has not written yet cannot have prompts,
  // and nothing the client does here will change that. It is a state to
  // explain, not a failure to retry, and adding a prompt has nowhere to go.
  const awaitingSetup = !review.isPending && !review.data?.config;

  const add = useAddMyPrompt();
  const update = useUpdateMyPrompt();
  const remove = useDeleteMyPrompt();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PromptPublic | null>(null);
  const [copying, setCopying] = useState<PromptPublic | null>(null);
  const close = () => {
    setAdding(false);
    setEditing(null);
    setCopying(null);
  };

  // Memoised so the dialog's "reset to initial when opened" effect sees one
  // object per prompt, not a new one every render.
  const source = editing ?? copying;
  const initial = useMemo<PromptDraft | undefined>(
    () =>
      source
        ? {
            text: source.text,
            frameworkId: source.frameworkId,
            contentPillarId: source.contentPillarId ?? null,
          }
        : undefined,
    [source],
  );

  const submit = (draft: PromptDraft) => {
    const body = {
      text: draft.text.trim(),
      frameworkId: draft.frameworkId,
      contentPillarId: draft.contentPillarId,
    };
    if (editing) {
      update.mutate({ path: { prompt_id: editing.id }, body }, { onSuccess: close });
      return;
    }
    add.mutate({ body }, { onSuccess: close });
  };

  return (
    <>
      <PageHeader
        eyebrow="Your team"
        title="Prompt library"
        description="Starting points your team writes posts from. The Motherhood wrote the first set; add your own whenever something comes up."
        actions={
          awaitingSetup ? null : (
            <Button onClick={() => setAdding(true)}>
              <Plus /> Add a prompt
            </Button>
          )
        }
      />

      {isPending || review.isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : awaitingSetup ? (
        <EmptyState
          title="Your brand setup is being prepared"
          description="The Motherhood writes your voice, your topics and your first prompts before the library opens. You will find them here once that is done."
        />
      ) : isError ? (
        <ErrorState
          title="Could not load your prompts"
          description="Something went wrong at our end. Try again in a moment."
          onRetry={() => void refetch()}
        />
      ) : prompts.length === 0 ? (
        <EmptyState
          title="No prompts yet"
          description="The Motherhood usually writes the first set during your brand setup. You can add your own at any time."
          action={
            <Button onClick={() => setAdding(true)}>
              <Plus /> Add the first one
            </Button>
          }
        />
      ) : (
        <PromptList
          prompts={prompts}
          emptyTitle="No prompts yet"
          emptyDescription="Add one your team can write from."
          onEdit={setEditing}
          onDelete={(prompt) => remove.mutate({ path: { prompt_id: prompt.id } })}
          onDuplicate={setCopying}
          busy={remove.isPending}
        />
      )}

      <PromptDialog
        open={adding || editing !== null || copying !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        pillars={pillars}
        initial={initial}
        copying={copying !== null}
        onSubmit={submit}
        saving={add.isPending || update.isPending}
      />
    </>
  );
}
